import { useEffect, useRef, useState } from 'react';
import { Bold, Italic, Underline, Link as LinkIcon, Image as ImageIcon, Heading1, Heading2 } from 'lucide-react';
import { MediaPicker } from './MediaPicker';
import { uploadMedia } from '../lib/supabase';

interface Props {
  html: string;
  onChange: (html: string) => void;
}

/**
 * Mail-Links folgen dem sichtbaren Text: Wer „[STANDORT]@randori-pro.de" mit
 * „tegel@randori-pro.de" überschreibt, ändert damit auch das Ziel des Links.
 */
function mailLinksAngleichen(doc: Document) {
  doc.querySelectorAll<HTMLAnchorElement>('a[href^="mailto:"]').forEach((a) => {
    const text = (a.textContent ?? '').trim();
    if (/^[^\s@<>[\]]+@[^\s@<>[\]]+\.[a-z]{2,}$/i.test(text) && a.getAttribute('href') !== `mailto:${text}`) {
      a.setAttribute('href', `mailto:${text}`);
    }
  });
}

function serialisieren(doc: Document): string {
  mailLinksAngleichen(doc);
  return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
}

/**
 * Brevo-Style Inline-Editor: Preview des HTML in einer iframe, in der
 * Texte direkt per Klick editierbar sind und Bilder über die Mediathek ersetzt werden.
 *
 * Zweck: bestehende HTML-Templates (ohne Unlayer-Design) trotzdem bearbeitbar machen.
 */
export function InlineTemplateEditor({ html, onChange }: Props) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const lastEmittedHtml = useRef<string>(html);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [replaceImgEl, setReplaceImgEl] = useState<HTMLImageElement | null>(null);
  const [uploadingInline, setUploadingInline] = useState(false);

  // iframe mit HTML befüllen und contenteditable + Bild-Click-Handler setzen
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;

      // HTML schreiben
      doc.open();
      doc.write(html);
      doc.close();

      // body contenteditable machen
      const body = doc.body;
      if (!body) return;
      body.setAttribute('contenteditable', 'true');
      body.style.outline = 'none';
      body.style.minHeight = '500px';

      // Klick-to-replace für alle Bilder
      const imgs = doc.querySelectorAll('img');
      imgs.forEach(img => {
        const el = img as HTMLImageElement;
        el.style.cursor = 'pointer';
        el.setAttribute('data-editable-img', 'true');
        el.addEventListener('mouseenter', () => {
          el.style.outline = '3px solid #6d1723';
          el.style.outlineOffset = '-2px';
        });
        el.addEventListener('mouseleave', () => {
          el.style.outline = '';
        });
        el.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          setReplaceImgEl(el);
          setMediaPickerOpen(true);
        });
      });

      // Änderungen emittieren (debounced)
      let timer: number | null = null;
      const emit = () => {
        if (timer) clearTimeout(timer);
        timer = window.setTimeout(() => {
          const newHtml = serialisieren(doc);
          if (newHtml !== lastEmittedHtml.current) {
            lastEmittedHtml.current = newHtml;
            onChange(newHtml);
          }
        }, 400);
      };
      body.addEventListener('input', emit);
      body.addEventListener('blur', emit, true);
    };

    iframe.addEventListener('load', handleLoad);
    // Trigger
    iframe.srcdoc = '<!DOCTYPE html><html><body>Lade...</body></html>';

    return () => { iframe.removeEventListener('load', handleLoad); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wenn sich html von außen ändert (z.B. Template-Wechsel) → neu laden
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument) return;
    if (html === lastEmittedHtml.current) return;
    // Neu laden: Reset
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    lastEmittedHtml.current = html;

    // Re-bind
    const body = doc.body;
    if (!body) return;
    body.setAttribute('contenteditable', 'true');
    body.style.outline = 'none';
    body.style.minHeight = '500px';
    const imgs = doc.querySelectorAll('img');
    imgs.forEach(img => {
      const el = img as HTMLImageElement;
      el.style.cursor = 'pointer';
      el.addEventListener('mouseenter', () => { el.style.outline = '3px solid #6d1723'; el.style.outlineOffset = '-2px'; });
      el.addEventListener('mouseleave', () => { el.style.outline = ''; });
      el.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        setReplaceImgEl(el);
        setMediaPickerOpen(true);
      });
    });
    const emit = () => {
      const newHtml = serialisieren(doc);
      if (newHtml !== lastEmittedHtml.current) {
        lastEmittedHtml.current = newHtml;
        onChange(newHtml);
      }
    };
    body.addEventListener('input', emit);
    body.addEventListener('blur', emit, true);
  }, [html, onChange]);

  // Toolbar-Befehle wirken auf das Dokument im iframe
  const exec = (command: string, value?: string) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.execCommand(command, false, value);
    // Aktuellen HTML-Stand emittieren
    setTimeout(() => {
      const newHtml = serialisieren(doc);
      lastEmittedHtml.current = newHtml;
      onChange(newHtml);
    }, 50);
  };

  const handleMediaSelect = (url: string) => {
    const doc = iframeRef.current?.contentDocument;
    if (replaceImgEl) {
      replaceImgEl.src = url;
    } else if (doc) {
      // Neues Bild einfügen an Cursor-Position
      doc.execCommand('insertImage', false, url);
    }
    if (doc) {
      const newHtml = serialisieren(doc);
      lastEmittedHtml.current = newHtml;
      onChange(newHtml);
    }
    setMediaPickerOpen(false);
    setReplaceImgEl(null);
  };

  const handleInlineUpload = async (file: File) => {
    setUploadingInline(true);
    try {
      const item = await uploadMedia(file, 'inline-edit');
      handleMediaSelect(item.url);
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setUploadingInline(false);
    }
  };

  const insertLink = () => {
    const url = prompt('Link-URL:');
    if (url) exec('createLink', url);
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: '0.25rem', padding: '0.5rem',
        background: 'var(--surface-secondary)',
        border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
        borderBottom: 'none',
      }}>
        <button className="btn btn-ghost btn-sm" onClick={() => exec('bold')} title="Fett (Strg+B)"><Bold size={14} /></button>
        <button className="btn btn-ghost btn-sm" onClick={() => exec('italic')} title="Kursiv (Strg+I)"><Italic size={14} /></button>
        <button className="btn btn-ghost btn-sm" onClick={() => exec('underline')} title="Unterstrichen (Strg+U)"><Underline size={14} /></button>
        <div style={{ width: 1, background: 'var(--border-color)', margin: '0 0.25rem' }} />
        <button className="btn btn-ghost btn-sm" onClick={() => exec('formatBlock', 'H1')} title="Überschrift 1"><Heading1 size={14} /></button>
        <button className="btn btn-ghost btn-sm" onClick={() => exec('formatBlock', 'H2')} title="Überschrift 2"><Heading2 size={14} /></button>
        <button className="btn btn-ghost btn-sm" onClick={() => exec('formatBlock', 'P')} title="Normaler Absatz">P</button>
        <div style={{ width: 1, background: 'var(--border-color)', margin: '0 0.25rem' }} />
        <button className="btn btn-ghost btn-sm" onClick={insertLink} title="Link einfügen"><LinkIcon size={14} /></button>
        <button className="btn btn-ghost btn-sm" onClick={() => { setReplaceImgEl(null); setMediaPickerOpen(true); }} title="Bild einfügen">
          <ImageIcon size={14} />
        </button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', alignSelf: 'center', marginRight: '0.5rem' }}>
          Klick in Text zum Bearbeiten · Klick auf Bild zum Ersetzen
        </span>
      </div>

      <iframe
        ref={iframeRef}
        style={{
          width: '100%', height: 650,
          border: '1px solid var(--border-color)',
          borderRadius: '0 0 var(--radius-md) var(--radius-md)',
          background: 'white',
        }}
        title="Template bearbeiten"
        sandbox="allow-same-origin"
      />

      {mediaPickerOpen && (
        <MediaPicker
          onSelect={handleMediaSelect}
          onUpload={handleInlineUpload}
          uploading={uploadingInline}
          onClose={() => { setMediaPickerOpen(false); setReplaceImgEl(null); }}
          title={replaceImgEl ? 'Bild ersetzen' : 'Bild einfügen'}
        />
      )}
    </div>
  );
}
